import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Play, Pause, Crown, Lock, Check, Loader2, LogIn, Mic, Square, Send, Volume2, VolumeX,
  Drum, AudioWaveform, Gem, Timer, KeyRound, ExternalLink, MessageSquare, Gift, Download,
  Waves, Filter, Rewind, BookOpen, Ghost, Gamepad2,
} from "lucide-react";
import { API, BACKEND } from "@/assets";
import { useSiteAuth, signInWithGoogle } from "@/components/GoogleAuth";
import { LicenseModal } from "@/components/LicenseModal";
import { MedusaEngine, blobToWavFile } from "@/components/medusa/engine";
import { useInstallPrompt } from "@/components/useInstallPrompt";
import { PlayerCard } from "@/components/medusa/PlayerCard";
import PianoScales from "@/components/medusa/PianoScales";
import { VenomDropQuiz, SerpentSpiral, DropTheBass, BeatBuilder, GAME_META } from "@/components/medusa/ArcadeGames";

// Gorgon Streak — consecutive-day visit streak kept on the device
function useGorgonStreak() {
  return useMemo(() => {
    try {
      const raw = localStorage.getItem("medusa_streak");
      const today = new Date().toDateString();
      let s = raw ? JSON.parse(raw) : { count: 0, last: "" };
      if (s.last !== today) {
        const yesterday = new Date(Date.now() - 86400000).toDateString();
        s = { count: s.last === yesterday ? s.count + 1 : 1, last: today };
        localStorage.setItem("medusa_streak", JSON.stringify(s));
      }
      return s.count;
    } catch { return 1; }
  }, []);
}

/* ---------------- styles ---------------- */
const ROOM_CSS = `
@keyframes medusa-float { 0%,100% { transform: translateY(0) rotate(-1.2deg) } 50% { transform: translateY(-12px) rotate(1.2deg) } }
@keyframes medusa-pulse { 0%,100% { opacity: .55; transform: scale(1) } 50% { opacity: .9; transform: scale(1.06) } }
@keyframes medusa-glowcycle {
  0%,100% { filter: drop-shadow(0 0 30px rgba(236,72,153,.55)) drop-shadow(0 0 90px rgba(168,85,247,.35)); }
  25% { filter: drop-shadow(0 0 30px rgba(6,182,212,.55)) drop-shadow(0 0 90px rgba(236,72,153,.3)); }
  50% { filter: drop-shadow(0 0 30px rgba(245,184,76,.55)) drop-shadow(0 0 90px rgba(236,72,153,.32)); }
  75% { filter: drop-shadow(0 0 30px rgba(57,255,20,.45)) drop-shadow(0 0 90px rgba(6,182,212,.3)); }
}
@keyframes smokeDrift {
  0% { transform: translateX(0) scaleY(1); }
  50% { transform: translateX(-3%) scaleY(1.05); }
  100% { transform: translateX(0) scaleY(1); }
}
@keyframes smokeDriftSlow {
  0% { transform: translateX(-2%) scaleY(1.03); }
  50% { transform: translateX(1.5%) scaleY(1); }
  100% { transform: translateX(-2%) scaleY(1.03); }
}
.animate-smoke-drift { animation: smokeDrift 12s ease-in-out infinite; }
.animate-smoke-drift-slow { animation: smokeDriftSlow 19s ease-in-out infinite; }
.medusa-alive { animation: medusa-float 5s ease-in-out infinite, medusa-glowcycle 9s ease-in-out infinite; transition: transform .25s ease-out; transform-style: preserve-3d; will-change: transform, filter; }
@keyframes medusa-marquee { from { transform: translateX(0) } to { transform: translateX(-50%) } }
@keyframes crystal-shimmer { 0%,100% { filter: drop-shadow(0 0 6px currentColor) } 50% { filter: drop-shadow(0 0 18px currentColor) } }
@keyframes vault-sweep { from { transform: translateX(-110%) skewX(-12deg) } to { transform: translateX(220%) skewX(-12deg) } }
@keyframes stone-in { from { filter: grayscale(0) brightness(1) } to { filter: grayscale(1) brightness(.55) contrast(1.1) } }
@keyframes gold-reveal { from { opacity: 0; transform: translateY(14px) scale(.98) } to { opacity: 1; transform: translateY(0) scale(1) } }
.medusa-marquee-track { display: inline-flex; white-space: nowrap; animation: medusa-marquee 46s linear infinite; }
.stone-locked { animation: stone-in 1.2s ease forwards; }
`;

/* ---------------- particles ---------------- */
function ParticleField() {
  const ref = useRef(null);
  const mouse = useRef({ x: 0.5, y: 0.5 });
  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    let raf;
    const colors = ["#A855F7", "#06B6D4", "#39FF14", "#EC4899", "#F5B84C"];
    const dots = Array.from({ length: 70 }, () => ({
      x: Math.random(), y: Math.random(), r: Math.random() * 2 + 0.4,
      vx: (Math.random() - 0.5) * 0.0004, vy: (Math.random() - 0.5) * 0.0004,
      c: colors[Math.floor(Math.random() * colors.length)],
      depth: Math.random() * 0.8 + 0.2, tw: Math.random() * Math.PI * 2,
      scale: Math.random() < 0.18, // shimmering snake-scale diamonds
    }));
    const resize = () => { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight; };
    resize();
    window.addEventListener("resize", resize);
    const onMove = (e) => { mouse.current = { x: e.clientX / window.innerWidth, y: e.clientY / window.innerHeight }; };
    window.addEventListener("pointermove", onMove);
    const px = { x: 0.5, y: 0.5 };
    const draw = (t) => {
      px.x += (mouse.current.x - px.x) * 0.04;
      px.y += (mouse.current.y - px.y) * 0.04;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      dots.forEach((d) => {
        d.x = (d.x + d.vx + 1) % 1;
        d.y = (d.y + d.vy + 1) % 1;
        const ox = (px.x - 0.5) * 40 * d.depth;
        const oy = (px.y - 0.5) * 40 * d.depth;
        const X = d.x * canvas.width + ox;
        const Y = d.y * canvas.height + oy;
        const a = 0.25 + 0.55 * Math.abs(Math.sin(t / 900 + d.tw));
        ctx.globalAlpha = a;
        ctx.fillStyle = d.c;
        if (d.scale) {
          ctx.save();
          ctx.translate(X, Y);
          ctx.rotate(Math.PI / 4);
          ctx.fillRect(-d.r, -d.r, d.r * 2, d.r * 2);
          ctx.restore();
        } else {
          ctx.beginPath();
          ctx.arc(X, Y, d.r, 0, Math.PI * 2);
          ctx.fill();
        }
      });
      ctx.globalAlpha = 1;
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); window.removeEventListener("pointermove", onMove); };
  }, []);
  return <canvas ref={ref} className="absolute inset-0 w-full h-full pointer-events-none" aria-hidden="true" data-testid="medusa-particles" />;
}

/* ---------------- waveform visualizer ---------------- */
function WaveViz({ engine, active }) {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    let raf;
    const draw = () => {
      const w = canvas.width = canvas.offsetWidth;
      const h = canvas.height = canvas.offsetHeight;
      ctx.clearRect(0, 0, w, h);
      const an = engine.current?.analyser;
      if (an && active) {
        const data = new Uint8Array(an.frequencyBinCount);
        an.getByteTimeDomainData(data);
        const grad = ctx.createLinearGradient(0, 0, w, 0);
        grad.addColorStop(0, "#A855F7"); grad.addColorStop(0.5, "#EC4899"); grad.addColorStop(1, "#06B6D4");
        ctx.strokeStyle = grad;
        ctx.lineWidth = 2;
        ctx.beginPath();
        const step = w / data.length;
        data.forEach((v, i) => {
          const y = (v / 255) * h;
          i === 0 ? ctx.moveTo(0, y) : ctx.lineTo(i * step, y);
        });
        ctx.stroke();
      } else {
        ctx.strokeStyle = "rgba(168,85,247,0.25)";
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(0, h / 2);
        ctx.lineTo(w, h / 2);
        ctx.stroke();
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [engine, active]);
  return <canvas ref={ref} className="w-full h-24 sm:h-28" data-testid="medusa-waveform" />;
}

/* ---------------- shared bits ---------------- */
const PERKS = [
  "Unreleased tracks & early demos before anyone else hears them",
  "Members-only vault drops — stems, loops, behind-the-scenes",
  "First access to limited Boa's 33rd Dimension™ merch runs",
  "Priority A&R feedback on your submissions",
];
const PLANS = [
  { id: "monthly", name: "Monthly", price: "$9.99", per: "/month", note: "7-day free trial — then $9.99/mo. Cancel anytime.", featured: true, trial: true, testId: "medusa-plan-monthly" },
  { id: "yearly", name: "Yearly", price: "$99", per: "/year", note: "Two months free — best value.", testId: "medusa-plan-yearly" },
];

const STEMS = [
  { id: "drums", label: "Drums", Icon: Drum, neon: "#EC4899" },
  { id: "bass", label: "Bass", Icon: AudioWaveform, neon: "#06B6D4" },
  { id: "melody", label: "Melody", Icon: Gem, neon: "#A855F7" },
  { id: "vox", label: "Vocals", Icon: Mic, neon: "#39FF14" },
];

const CRYSTALS = [
  { id: "amethyst", label: "Amethyst", color: "#A855F7", hint: "the violet crown" },
  { id: "obsidian", label: "Obsidian", color: "#52525B", hint: "the stone that drinks the light" },
  { id: "emerald", label: "Emerald", color: "#39FF14", hint: "the serpent's heart" },
];
const CRYSTAL_SEQUENCE = ["amethyst", "obsidian", "emerald"];

const TICKER_ITEMS = [
  "Artist from Atlanta just secured an Exclusive license",
  "New vocal demo submitted to the Snake Pit",
  "Producer in Houston copped a WAV Lease",
  "Medusa Room VIP just unlocked the Crystal Vault",
  "Unlimited license secured on a Cold-Blooded Soundscape",
  "A&R moved a new demo to Callback",
  "Artist from Augusta locked in an MP3 Lease",
  "Vault drop claimed by a Yearly member",
  "Studio session block booked for this weekend",
  "Exclusive rights just went vault-only on a Heavy 808s cut",
];

const fmtClock = (s) => `${String(Math.floor(s / 3600)).padStart(2, "0")}:${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

/* ================= page ================= */
export default function MedusaRoomPage() {
  const { user, profile } = useSiteAuth();
  const engine = useRef(null);
  if (!engine.current && typeof window !== "undefined") engine.current = new MedusaEngine();

  const [entered, setEntered] = useState(false);
  const [muted, setMuted] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [stems, setStems] = useState({ drums: true, bass: true, melody: true, vox: true });
  const [venom, setVenom] = useState(0);
  const [bonusOpen, setBonusOpen] = useState(false);
  const [glow, setGlow] = useState({ x: 50, y: 40 });

  // membership
  const [status, setStatus] = useState(null);
  const [vault, setVault] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  // catalog
  const [tracks, setTracks] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [isPreviewPlaying, setIsPreviewPlaying] = useState(false);
  const [licenseTrack, setLicenseTrack] = useState(null);
  const [buying, setBuying] = useState(false);
  const previewAudio = useRef(null);

  // crystal vault
  const [crystalPicks, setCrystalPicks] = useState([]);
  const [crystalState, setCrystalState] = useState("idle"); // idle | wrong | unlocked

  // snake pit
  const [recState, setRecState] = useState("idle"); // idle | recording | done
  const [seed, setSeed] = useState(null);
  useEffect(() => {
    fetch(`${API}/serpent-seed`).then((r) => r.json()).then((d) => setSeed(d.seed)).catch(() => {});
  }, []);

  // venom FX pad (mixer effects)
  const [fx, setFx] = useState({ reverb: false, lowpass: false });
  const toggleFx = (name) => {
    const on = !fx[name];
    setFx((f) => ({ ...f, [name]: on }));
    if (name === "reverb") engine.current.toggleReverb(on);
    if (name === "lowpass") engine.current.toggleLowpass(on);
  };
  const hitTapeStop = () => engine.current.tapeStop();

  // ghost verses — featured Snake Pit auditions from the label
  const [ghosts, setGhosts] = useState(null);
  const [ghostPlaying, setGhostPlaying] = useState(null);
  const ghostAudio = useRef(null);
  useEffect(() => {
    fetch(`${API}/ghost-verses`).then((r) => (r.ok ? r.json() : { verses: [] }))
      .then((d) => setGhosts(d.verses || [])).catch(() => setGhosts([]));
  }, []);
  const toggleGhost = (tId) => {
    if (ghostPlaying === tId) {
      ghostAudio.current?.pause(); setGhostPlaying(null); return;
    }
    ghostAudio.current?.pause();
    const a = new Audio(`${API}/ghost-audio/${tId}`);
    a.onended = () => setGhostPlaying(null);
    a.play().catch(() => setGhostPlaying(null));
    ghostAudio.current = a;
    setGhostPlaying(tId);
  };
  useEffect(() => () => ghostAudio.current?.pause(), []);
  const [recSeconds, setRecSeconds] = useState(0);
  const [recUrl, setRecUrl] = useState(null);
  const [recBlob, setRecBlob] = useState(null);
  const [recName, setRecName] = useState("");
  const [recEmail, setRecEmail] = useState("");
  const [recNote, setRecNote] = useState("");
  const [recPlaying, setRecPlaying] = useState(false);
  const mediaRec = useRef(null);
  const recTimer = useRef(null);
  const recPlayback = useRef(null);

  const enterRoom = () => {
    setEntered(true);
    engine.current.startAmbient();
  };
  const toggleMute = () => {
    if (muted) { engine.current.startAmbient(); setMuted(false); }
    else { engine.current.stopAmbient(); setMuted(true); }
  };

  // stem playback + venom meter
  const togglePlay = () => {
    if (playing) { engine.current.stopStems(); setPlaying(false); }
    else { engine.current.startStems(); setPlaying(true); }
  };
  const toggleStem = (id) => {
    const next = { ...stems, [id]: !stems[id] };
    setStems(next);
    engine.current.setStem(id, next[id]);
  };
  useEffect(() => {
    if (!playing || venom >= 100) return;
    const t = setInterval(() => setVenom((v) => Math.min(100, v + 100 / 75)), 1000); // ~75s of listening to fill
    return () => clearInterval(t);
  }, [playing, venom]);
  useEffect(() => { if (venom >= 100) setBonusOpen(true); }, [venom]);

  // membership status
  useEffect(() => {
    fetch(`${API}/medusa/status`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then(setStatus).catch(() => setStatus({ signed_in: false, member: false }));
  }, [user]);
  useEffect(() => {
    if (!status?.member) { setVault(null); return; }
    fetch(`${API}/vault`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setVault(d?.items || [])).catch(() => setVault([]));
  }, [status]);

  const subscribe = async (plan) => {
    setError("");
    if (!user) { signInWithGoogle(); return; }
    setBusy(plan);
    try {
      const res = await fetch(`${API}/medusa/subscribe`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan, origin_url: window.location.origin }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Checkout failed to start.");
      window.location.href = data.checkout_url;
    } catch (e) { setError(e.message); setBusy(""); }
  };

  // catalog
  useEffect(() => {
    fetch(`${API}/tracks`).then((r) => r.json()).then((d) => setTracks(d.tracks || [])).catch(() => {});
  }, []);
  const moodGroups = useMemo(() => {
    const groups = {};
    tracks.forEach((t) => {
      const mood = t.mood || "Cold-Blooded Soundscapes";
      (groups[mood] = groups[mood] || []).push(t);
    });
    return groups;
  }, [tracks]);

  // petrified beat — admin-scheduled drop wins, else daily rotation
  const [now, setNow] = useState(Date.now());
  const [scheduledDrop, setScheduledDrop] = useState(null);
  useEffect(() => {
    fetch(`${API}/drops/today`).then((r) => r.json()).then((d) => {
      if (d.scheduled && d.track) setScheduledDrop(d.track);
    }).catch(() => {});
  }, []);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const midnight = useMemo(() => { const d = new Date(); d.setHours(24, 0, 0, 0); return d.getTime(); }, []);
  const petrified = useMemo(() => {
    if (scheduledDrop) return scheduledDrop;
    if (!tracks.length) return null;
    const doy = Math.floor(Date.now() / 86400000);
    return tracks[doy % tracks.length];
  }, [tracks, scheduledDrop]);
  const streak = useGorgonStreak();
  const installApp = useInstallPrompt();
  const [installHelp, setInstallHelp] = useState(false);
  // first-visit onboarding (skippable)
  const [tourStep, setTourStep] = useState(-1); // -1 = hidden
  useEffect(() => {
    if (entered && !localStorage.getItem("medusa_onboarded_v1")) setTourStep(0);
  }, [entered]);
  const closeTour = () => { localStorage.setItem("medusa_onboarded_v1", "1"); setTourStep(-1); };
  const secondsLeft = Math.max(0, Math.floor((midnight - now) / 1000));
  const petrifiedLocked = secondsLeft <= 0;

  const togglePreview = (track) => {
    if (!track.file) return;
    if (currentId === track.id && isPreviewPlaying) {
      previewAudio.current?.pause(); setIsPreviewPlaying(false); return;
    }
    if (previewAudio.current) previewAudio.current.pause();
    const a = new Audio(`${BACKEND}${track.file}`);
    a.onended = () => { setIsPreviewPlaying(false); setCurrentId(null); };
    a.play().catch(() => {});
    previewAudio.current = a;
    setCurrentId(track.id);
    setIsPreviewPlaying(true);
  };

  const buy = async (tier) => {
    if (!licenseTrack) return;
    setBuying(true);
    try {
      const res = await fetch(`${API}/payments/checkout`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lookup_key: tier.lookupKey, track_id: licenseTrack.id, origin_url: window.location.origin }),
      });
      if (!res.ok) throw new Error("checkout failed");
      const { checkout_url } = await res.json();
      window.location.href = checkout_url;
    } catch {
      window.location.href = `/quote?service=${encodeURIComponent("Beat Licensing")}&details=${encodeURIComponent(
        `Beat: "${licenseTrack.title}" — ${tier.name} license at $${tier.price} USD. (Online checkout was unavailable, please invoice me.)`)}`;
    }
  };

  // crystals
  const pickCrystal = (id) => {
    if (crystalState === "unlocked") return;
    const next = [...crystalPicks, id];
    setCrystalPicks(next);
    const ok = next.every((v, i) => v === CRYSTAL_SEQUENCE[i]);
    if (!ok) {
      setCrystalState("wrong");
      setTimeout(() => { setCrystalPicks([]); setCrystalState("idle"); }, 900);
      return;
    }
    if (next.length === CRYSTAL_SEQUENCE.length) setCrystalState("unlocked");
  };

  // snake pit
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      const chunks = [];
      mr.ondataavailable = (e) => chunks.push(e.data);
      mr.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks, { type: mr.mimeType || "audio/webm" });
        setRecBlob(blob);
        setRecUrl(URL.createObjectURL(blob));
        setRecState("done");
      };
      mediaRec.current = mr;
      mr.start();
      setRecState("recording");
      setRecSeconds(0);
      recTimer.current = setInterval(() => {
        setRecSeconds((s) => {
          if (s + 1 >= 30) { stopRecording(); return 30; }
          return s + 1;
        });
      }, 1000);
    } catch {
      setRecNote("Microphone access was blocked — allow mic permission and try again.");
    }
  };
  const stopRecording = () => {
    if (recTimer.current) clearInterval(recTimer.current);
    if (mediaRec.current && mediaRec.current.state !== "inactive") mediaRec.current.stop();
  };
  const toggleRecPlayback = () => {
    if (!recUrl) return;
    if (recPlaying) { recPlayback.current?.pause(); setRecPlaying(false); return; }
    const a = new Audio(recUrl);
    a.onended = () => setRecPlaying(false);
    a.play().catch(() => {});
    recPlayback.current = a;
    setRecPlaying(true);
  };
  const sendDemo = async () => {
    if (!recBlob || !recName.trim() || !/.+@.+\..+/.test(recEmail)) {
      setRecNote("Add your artist name and a valid email before sending.");
      return;
    }
    setBusy("demo"); setRecNote("");
    try {
      const wav = await blobToWavFile(recBlob);
      const fd = new FormData();
      fd.append("stage_name", recName.trim());
      fd.append("email", recEmail.trim());
      fd.append("message", "Snake Pit vocal idea (recorded live over a Medusa Room beat)");
      fd.append("demo", wav);
      const res = await fetch(`${API}/talent`, { method: "POST", body: fd });
      if (!res.ok) throw new Error("send failed");
      setRecNote("Sent to the label — A&R will hear it. Watch your inbox.");
      setRecState("idle"); setRecBlob(null); setRecUrl(null); setRecSeconds(0);
    } catch {
      setRecNote("Send failed — try again or use the full demo form on the Talent page.");
    } finally { setBusy(""); }
  };

  const neonText = { textShadow: "0 0 18px rgba(168,85,247,0.9), 0 0 42px rgba(236,72,153,0.5)" };

  /* ---- enter gate (browsers require a gesture before audio) ---- */
  if (!entered) {
    return (
      <main className="min-h-screen bg-[#050507] text-ice flex items-center justify-center relative overflow-hidden" data-testid="medusa-gate">
        <style>{ROOM_CSS}</style>
        <ParticleField />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(236,72,153,0.16),transparent_60%)]" />
        <div className="relative text-center px-6">
          <img src="/medusa-logo.webp" alt="Cold-Blooded Records Medusa — serpent hair and floral crown" className="w-56 sm:w-72 mx-auto medusa-alive" data-testid="medusa-gate-logo" />
          <h1 className="mt-8 font-display text-4xl sm:text-6xl font-bold text-white" style={neonText} data-testid="medusa-gate-title">THE MEDUSA ROOM</h1>
          <p className="mt-4 text-sm sm:text-base text-ice/55 max-w-md mx-auto">An immersive members' chamber by DGE: Cold-Blooded Records LLC™. Sound on — the room breathes.</p>
          <button onClick={enterRoom} className="mt-8 inline-flex items-center gap-2.5 bg-gradient-to-r from-purple-500 via-fuchsia-500 to-pink-500 text-white font-bold text-sm px-10 py-4 rounded-full shadow-[0_0_30px_rgba(168,85,247,0.5)] hover:shadow-[0_0_46px_rgba(236,72,153,0.7)] active:scale-95 transition-all" data-testid="medusa-enter-button">
            <KeyRound size={17} /> Enter the Room
          </button>
          <button onClick={() => { setMuted(true); setEntered(true); }} className="mt-4 block mx-auto text-[11px] font-mono uppercase tracking-[0.2em] text-ice/40 hover:text-ice/70 transition-colors" data-testid="medusa-enter-silent">Enter silently</button>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#050507] text-ice relative" data-testid="medusa-room">
      <style>{ROOM_CSS}</style>

      {tourStep >= 0 && (
        <div className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-sm flex items-center justify-center p-5" data-testid="medusa-onboarding">
          <div className="w-full max-w-md bg-[#0E0E12] border border-purple-500/40 rounded-3xl p-8 shadow-[0_0_60px_rgba(168,85,247,0.25)]">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-fuchsia-400">First Time in the Room · {tourStep + 1}/3</p>
            {(() => {
              const [t, b] = [
                ["This is a place to get dangerous.", "The Medusa Room is a training ground disguised as a playground. Every section trains a real musical skill — and everything you do earns XP toward your Medusa Level."],
                ["Start with the doors.", "Six portals: Piano Mastery and the Production Academy build skills; the Arcade pays prizes; the Snake Pit puts your voice in front of A&R; the Vault and Studio are member territory."],
                ["Sign in to keep it.", "Guests can play everything. But XP, levels, streaks, unlocks, and prize claims only stick to a player account — Google or email, free, 20 seconds."],
              ][tourStep];
              return (
                <div>
                  <h3 className="mt-3 font-display text-xl font-bold text-white" data-testid="onboarding-title">{t}</h3>
                  <p className="mt-2 text-sm text-ice/60 leading-relaxed">{b}</p>
                </div>
              );
            })()}
            <div className="mt-6 flex items-center justify-between">
              <button onClick={closeTour} className="font-mono text-[10px] uppercase tracking-[0.15em] text-ice/40 hover:text-ice/70 transition-colors" data-testid="onboarding-skip">Skip</button>
              {tourStep < 2 ? (
                <button onClick={() => setTourStep(tourStep + 1)} className="bg-gradient-to-r from-purple-500 to-fuchsia-500 text-white font-bold text-xs px-6 py-3 rounded-full active:scale-95 transition-all" data-testid="onboarding-next">Next</button>
              ) : (
                <button onClick={closeTour} className="bg-gradient-to-r from-yellow-400 to-amber-500 text-black font-bold text-xs px-6 py-3 rounded-full active:scale-95 transition-all" data-testid="onboarding-done">Enter the Room</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* atmosphere toggle */}
      <button onClick={toggleMute} className="fixed bottom-12 left-4 z-40 inline-flex items-center gap-2 bg-[#0E0E12]/90 border border-purple-500/40 rounded-full px-4 py-2.5 text-[10px] font-mono uppercase tracking-[0.15em] text-purple-300 hover:border-pink-500/60 transition-all backdrop-blur" data-testid="medusa-mute-toggle">
        {muted ? <VolumeX size={14} /> : <Volume2 size={14} />} <span className="hidden sm:inline">{muted ? "Unmute Atmosphere" : "Mute Atmosphere"}</span>
      </button>

      {/* HERO */}
      <section
        className="relative overflow-hidden pt-24 sm:pt-28 pb-14"
        onPointerMove={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          setGlow({ x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 });
        }}
        data-testid="medusa-hero"
      >
        <ParticleField />
        <div className="absolute inset-0 pointer-events-none transition-[background] duration-200" style={{ background: `radial-gradient(440px circle at ${glow.x}% ${glow.y}%, rgba(236,72,153,0.15), rgba(168,85,247,0.07) 40%, transparent 65%)` }} />
        <div className="relative max-w-5xl mx-auto px-5 sm:px-8 text-center">
          <div className="relative inline-block" style={{ perspective: "900px" }}>
            <div className="absolute -inset-10 bg-[radial-gradient(circle,rgba(236,72,153,0.3),rgba(245,158,11,0.12)_55%,transparent_75%)] blur-2xl" style={{ animation: "medusa-pulse 3.2s ease-in-out infinite" }} />
            <div style={{
              transform: `rotateX(${(50 - glow.y) * 0.16}deg) rotateY(${(glow.x - 50) * 0.16}deg)`,
              transition: "transform .3s ease-out", transformStyle: "preserve-3d",
            }} data-testid="medusa-logo-tilt">
              <img src="/medusa-logo.webp" alt="Cold-Blooded Records Medusa — serpent hair and floral crown" className="relative w-64 sm:w-96 mx-auto medusa-alive" data-testid="medusa-logo" />
            </div>
          </div>
          <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.3em] text-emerald-400" data-testid="medusa-eyebrow">Cold-Blooded Soundscapes™ · Members' Chamber</p>
          <h1 className="mt-3 font-display text-4xl sm:text-6xl font-bold text-white" style={neonText} data-testid="medusa-title">THE MEDUSA ROOM</h1>
          <p className="mt-4 text-base sm:text-lg text-ice/60 max-w-2xl mx-auto leading-relaxed">Mix the stems. Cut a vocal in the Snake Pit. Crack the Crystal Vault. Look too long and the beats turn to stone.</p>
          {status?.member && (
            <div className="mt-5 inline-flex items-center gap-2 border border-yellow-500/50 text-yellow-300 rounded-full px-5 py-2 font-mono text-[11px] uppercase tracking-[0.15em]" data-testid="medusa-active-badge">
              <Crown size={14} /> {status.plan} VIP · {status.status}
            </div>
          )}
          <div className="mt-4 inline-flex items-center gap-2 border border-emerald-500/40 text-emerald-300 rounded-full px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] ml-0 sm:ml-3" data-testid="gorgon-streak">
            <AudioWaveform size={13} /> Gorgon Streak · Day {streak}
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
            <button
              onClick={async () => { if (!(await installApp.promptInstall())) setInstallHelp((v) => !v); }}
              className="inline-flex items-center gap-2 border border-yellow-500/50 text-yellow-300 rounded-full px-5 py-2 font-mono text-[10px] uppercase tracking-[0.15em] hover:bg-yellow-500/10 transition-all"
              data-testid="medusa-install-app"
            >
              <Download size={13} /> {installApp.installed ? "App Installed ✓" : "Install the Medusa App"}
            </button>
          </div>
          {installHelp && !installApp.canInstall && !installApp.installed && (
            <p className="mt-3 mx-auto max-w-md text-[11px] text-ice/50 leading-relaxed" data-testid="medusa-install-help">
              Android: browser menu → <span className="text-yellow-200">Install app / Add to Home screen</span>.
              iPhone: Share → <span className="text-yellow-200">Add to Home Screen</span>.
              Native store listings are coming next.
            </p>
          )}
          <div className="mt-5 flex justify-center">
            <PlayerCard compact />
          </div>
        </div>
      </section>

      {/* SECTION PORTALS — the hub selector */}
      <section className="relative max-w-6xl mx-auto px-5 sm:px-8 pb-12" data-testid="medusa-portals">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ice/45 mb-1.5">Choose Your Section</p>
        <h2 className="font-display text-xl sm:text-2xl font-bold text-white mb-6">Six Doors. Every One Makes You Better.</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { name: "Piano Mastery", desc: "Real lessons, then perform label songs note-for-note. Timing, accuracy, reading.", to: "/medusa/piano", color: "#FF3399", section: "piano", testId: "portal-piano" },
            { name: "Production Academy", desc: "Beat Memory, Fix the Mix, 808 Mastery — mixing skills that transfer to real sessions.", to: "/medusa/academy", color: "#00F0FF", section: "academy", testId: "portal-academy" },
            { name: "Free Play Studio", desc: "The full 3D virtual studio. Walk the console, shape the mix. Members only.", to: "/medusa/studio", color: "#FFB800", section: "studio", badge: "VIP", testId: "portal-studio" },
            { name: "Arcade Chamber", desc: "Five games, five leaderboards, real prizes from the vault.", to: "#medusa-game", color: "#D946EF", section: "arcade", testId: "portal-arcade" },
            { name: "Snake Pit", desc: "Cut a 30-second vocal over the room's beat and send it to A&R. Best sends become Ghost Verses.", to: "#medusa-snakepit", color: "#00FF66", section: "snakepit", testId: "portal-snakepit" },
            { name: "VIP Vault", desc: "Members-only drops behind the crystal sequence. $9.99/mo with a 7-day free trial.", to: "/vip-vault", color: "#A855F7", badge: "VIP", testId: "portal-vault" },
          ].map((p) => {
            const lvl = profile?.sections?.[p.section]?.level;
            const card = (
              <>
                <div className="flex items-center justify-between mb-3">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: p.color, boxShadow: `0 0 12px ${p.color}` }} />
                  <div className="flex items-center gap-2">
                    {p.badge && <span className="font-mono text-[8px] uppercase tracking-[0.15em] border border-yellow-500/50 text-yellow-300 rounded-full px-2 py-0.5">{p.badge}</span>}
                    {lvl > 1 && <span className="font-mono text-[9px] uppercase tracking-[0.12em] text-emerald-300" data-testid={`${p.testId}-level`}>Lv {lvl}</span>}
                  </div>
                </div>
                <p className="font-display text-white font-bold">{p.name}</p>
                <p className="mt-1.5 text-xs text-ice/50 leading-relaxed">{p.desc}</p>
              </>
            );
            const cls = "block text-left rounded-2xl border border-[#26262e] bg-[#0B0B10]/80 p-5 transition-all duration-300 hover:-translate-y-1 hover:bg-white/[0.03] active:scale-[0.99]";
            return p.to.startsWith("#") ? (
              <button key={p.name} onClick={() => document.getElementById(p.to.slice(1))?.scrollIntoView({ behavior: "smooth", block: "start" })} className={cls} data-testid={p.testId}>{card}</button>
            ) : (
              <Link key={p.name} to={p.to} className={cls} data-testid={p.testId}>{card}</Link>
            );
          })}
        </div>
      </section>

      {/* PETRIFICATION STEM MIXER */}
      <section className="relative max-w-5xl mx-auto px-5 sm:px-8 pb-16" data-testid="medusa-mixer">
        <div className="bg-[#0B0B10]/90 border border-purple-500/25 rounded-3xl p-6 sm:p-8 shadow-[0_0_60px_rgba(138,43,226,0.12)] backdrop-blur">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-pink-400">The Petrification Mixer</p>
              <h2 className="font-display text-xl sm:text-2xl font-bold text-white mt-1">Wake the Stems from Stone</h2>
            </div>
            <button onClick={togglePlay} className={`inline-flex items-center gap-2 font-bold text-sm px-7 py-3.5 rounded-full active:scale-95 transition-all ${playing ? "bg-white/10 text-white border border-white/20" : "bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-[0_0_26px_rgba(168,85,247,0.5)]"}`} data-testid="medusa-play-toggle">
              {playing ? <Pause size={16} /> : <Play size={16} />} {playing ? "Stop the Ritual" : "Play the Beat"}
            </button>
          </div>

          <WaveViz engine={engine} active={playing || !muted} />

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5" data-testid="medusa-stems">
            {STEMS.map(({ id, label, Icon, neon }) => {
              const on = stems[id];
              return (
                <button key={id} onClick={() => toggleStem(id)} className={`rounded-2xl border p-4 sm:p-5 text-center transition-all duration-300 ${on ? "bg-[#14101C]" : "bg-[#101014]"}`}
                  style={on ? { borderColor: neon, boxShadow: `0 0 22px ${neon}55, inset 0 0 18px ${neon}22` } : { borderColor: "#26262c", filter: "grayscale(1) brightness(0.8)" }}
                  data-testid={`stem-${id}`} aria-pressed={on}>
                  <Icon size={26} className="mx-auto transition-colors" style={{ color: on ? neon : "#52525B" }} />
                  <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: on ? neon : "#52525B" }}>{label}</p>
                  <p className="text-[9px] font-mono text-ice/30 mt-0.5">{on ? "ALIVE" : "STONE"}</p>
                </button>
              );
            })}
          </div>

          {/* venom FX pad */}
          <div className="mt-5" data-testid="medusa-venom-fx">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-teal-300 mb-2.5">Venom FX Pad</p>
            <div className="grid grid-cols-3 gap-2.5">
              <button onClick={() => toggleFx("reverb")} aria-pressed={fx.reverb}
                className={`rounded-xl border p-3.5 text-center transition-all duration-300 active:scale-95 ${fx.reverb ? "border-teal-400 bg-teal-500/10 shadow-[0_0_20px_rgba(0,240,255,0.3)]" : "border-[#26262c] bg-[#101014] hover:border-teal-500/40"}`}
                data-testid="fx-reverb">
                <Waves size={20} className={`mx-auto ${fx.reverb ? "text-teal-300" : "text-ice/35"}`} />
                <p className={`mt-1.5 font-mono text-[9px] uppercase tracking-[0.15em] ${fx.reverb ? "text-teal-300" : "text-ice/40"}`}>Dungeon Reverb</p>
              </button>
              <button onClick={hitTapeStop}
                className="rounded-xl border border-[#26262c] bg-[#101014] p-3.5 text-center transition-all duration-300 hover:border-yellow-500/50 active:scale-90"
                data-testid="fx-tapestop">
                <Rewind size={20} className="mx-auto text-yellow-300/80" />
                <p className="mt-1.5 font-mono text-[9px] uppercase tracking-[0.15em] text-ice/40">Tape Stop</p>
              </button>
              <button onClick={() => toggleFx("lowpass")} aria-pressed={fx.lowpass}
                className={`rounded-xl border p-3.5 text-center transition-all duration-300 active:scale-95 ${fx.lowpass ? "border-pink-400 bg-pink-500/10 shadow-[0_0_20px_rgba(255,51,153,0.3)]" : "border-[#26262c] bg-[#101014] hover:border-pink-500/40"}`}
                data-testid="fx-lowpass">
                <Filter size={20} className={`mx-auto ${fx.lowpass ? "text-pink-300" : "text-ice/35"}`} />
                <p className={`mt-1.5 font-mono text-[9px] uppercase tracking-[0.15em] ${fx.lowpass ? "text-pink-300" : "text-ice/40"}`}>Low-Pass</p>
              </button>
            </div>
            <p className="mt-2 text-[10px] text-ice/35">FX hit the live mix — start the beat, then twist the sound. Tape Stop is a one-shot drop.</p>
          </div>

          {/* venom meter */}
          <div className="mt-6" data-testid="medusa-venom-meter">
            <div className="flex items-center justify-between mb-1.5">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-400">Venom Meter</span>
              <span className="font-mono text-[10px] text-ice/50" data-testid="venom-percent">{Math.floor(venom)}%</span>
            </div>
            <div className="h-2.5 rounded-full bg-white/8 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-emerald-400 via-lime-400 to-emerald-300 transition-all duration-700" style={{ width: `${venom}%`, boxShadow: "0 0 14px rgba(57,255,20,0.6)" }} />
            </div>
            <p className="mt-1.5 text-[11px] text-ice/40">{bonusOpen ? "Venom maxed — your reward is unlocked below." : "Keep the beat playing — at 100% the room rewards you."}</p>
          </div>

          {bonusOpen && (
            <div className="mt-5 rounded-2xl border border-emerald-400/50 bg-emerald-950/20 p-5" style={{ animation: "gold-reveal .6s ease" }} data-testid="medusa-bonus">
              <div className="flex flex-wrap items-center gap-4">
                <Gift size={26} className="text-emerald-300 shrink-0" />
                <div className="flex-1 min-w-40">
                  <p className="font-display text-white font-bold">Venom Unlocked</p>
                  <p className="text-xs text-ice/60 mt-0.5">Code <span className="font-mono text-emerald-300 tracking-widest" data-testid="medusa-bonus-code">SERPENT20</span> — 20% off any lease when you request a quote. Plus a free taste of the catalog:</p>
                </div>
                <a href={`${BACKEND}/api/audio/venom-protocol.wav`} download className="inline-flex items-center gap-2 bg-emerald-400 text-black font-bold text-xs px-5 py-2.5 rounded-full hover:shadow-[0_0_20px_rgba(57,255,20,0.5)] active:scale-95 transition-all" data-testid="medusa-bonus-download">
                  <Download size={14} /> Free Beat Download
                </a>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* SNAKE PIT */}
      <section id="medusa-snakepit" className="relative max-w-5xl mx-auto px-5 sm:px-8 pb-16" data-testid="medusa-snakepit">
        <div className="bg-[#0B0B10]/90 border border-pink-500/25 rounded-3xl p-6 sm:p-8 backdrop-blur">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-pink-400">The Snake Pit</p>
          <h2 className="font-display text-xl sm:text-2xl font-bold text-white mt-1">Cut a Vocal Idea — Right Here</h2>
          <p className="text-sm text-ice/55 mt-2 max-w-xl">Press record, catch up to 30 seconds of your voice over the room's beat, play it back, and fire it straight to our A&R desk.</p>
          {seed && (
            <div className="mt-4 inline-flex items-center gap-3 border border-yellow-500/40 bg-yellow-950/15 rounded-2xl px-5 py-3.5 max-w-xl" data-testid="serpent-seed-card">
              <Gem size={18} className="text-yellow-300 shrink-0" />
              <p className="text-xs text-yellow-100/85 leading-relaxed"><span className="font-mono text-[9px] uppercase tracking-[0.2em] text-yellow-300 block mb-0.5">Serpent Seed · {seed.date}</span>{seed.prompt}</p>
            </div>
          )}
          <div className="mt-6 flex flex-wrap items-center gap-4">
            {recState !== "recording" ? (
              <button onClick={startRecording} className="inline-flex items-center gap-2.5 bg-gradient-to-r from-pink-500 to-fuchsia-500 text-white font-bold text-sm px-8 py-4 rounded-full shadow-[0_0_26px_rgba(236,72,153,0.5)] hover:shadow-[0_0_40px_rgba(236,72,153,0.7)] active:scale-95 transition-all" data-testid="snakepit-record">
                <Mic size={17} /> Record Idea
              </button>
            ) : (
              <button onClick={stopRecording} className="inline-flex items-center gap-2.5 bg-red-500 text-white font-bold text-sm px-8 py-4 rounded-full animate-pulse active:scale-95 transition-all" data-testid="snakepit-stop">
                <Square size={15} /> Stop ({30 - recSeconds}s left)
              </button>
            )}
            {recState === "done" && (
              <button onClick={toggleRecPlayback} className="inline-flex items-center gap-2 border border-teal-400/50 text-teal-300 font-bold text-xs px-6 py-3 rounded-full hover:bg-teal-400/10 transition-all" data-testid="snakepit-preview">
                {recPlaying ? <Pause size={14} /> : <Play size={14} />} {recPlaying ? "Pause" : "Preview"}
              </button>
            )}
            {recState === "recording" && <span className="font-mono text-xs text-red-400" data-testid="snakepit-timer">● REC {recSeconds}s / 30s</span>}
          </div>
          {recState === "done" && (
            <div className="mt-5 grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end" data-testid="snakepit-form">
              <input value={recName} onChange={(e) => setRecName(e.target.value)} placeholder="Artist name *" className="bg-[#14141A] border border-[#2a2a34] rounded-xl px-4 py-3 text-sm text-ice placeholder:text-ice/35 focus:outline-none focus:border-pink-400/70" data-testid="snakepit-name" />
              <input type="email" value={recEmail} onChange={(e) => setRecEmail(e.target.value)} placeholder="Email *" className="bg-[#14141A] border border-[#2a2a34] rounded-xl px-4 py-3 text-sm text-ice placeholder:text-ice/35 focus:outline-none focus:border-pink-400/70" data-testid="snakepit-email" />
              <button onClick={sendDemo} disabled={busy === "demo"} className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-pink-500 to-purple-500 text-white font-bold text-xs px-6 py-3.5 rounded-full hover:shadow-[0_0_22px_rgba(236,72,153,0.6)] active:scale-95 disabled:opacity-60 transition-all" data-testid="snakepit-send">
                {busy === "demo" ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send to Label
              </button>
            </div>
          )}
          {recNote && <p className="mt-3 text-xs text-ice/60" data-testid="snakepit-note">{recNote}</p>}

          {/* ghost verses — community auditions featured by the label */}
          {ghosts && ghosts.length > 0 && (
            <div className="mt-7 border-t border-pink-500/15 pt-6" data-testid="ghost-verses">
              <div className="flex items-center gap-2.5 mb-1">
                <Ghost size={16} className="text-teal-300" />
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-teal-300">Ghost Verses</p>
              </div>
              <p className="text-xs text-ice/50 max-w-xl">Auditions cut right here in the Pit and hand-picked by the label. Press play — the best Snake Pit sends go public.</p>
              <div className="mt-4 grid sm:grid-cols-2 gap-2.5">
                {ghosts.map((g) => (
                  <button key={g.t_id} onClick={() => toggleGhost(g.t_id)}
                    className={`flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition-all active:scale-[0.98] ${ghostPlaying === g.t_id ? "border-teal-400 bg-teal-500/10 shadow-[0_0_18px_rgba(0,240,255,0.25)]" : "border-[#26262c] bg-black/40 hover:border-teal-500/40"}`}
                    data-testid={`ghost-${g.t_id}`}>
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${ghostPlaying === g.t_id ? "bg-teal-400 text-black" : "bg-teal-500/15 text-teal-300"}`}>
                      {ghostPlaying === g.t_id ? <Pause size={13} /> : <Play size={13} />}
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm text-white font-semibold truncate">{g.stage_name}</span>
                      <span className="block font-mono text-[9px] uppercase tracking-[0.15em] text-ice/40">{ghostPlaying === g.t_id ? "Haunting the room…" : "Snake Pit audition"}</span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ARCADE CHAMBER — five trials */}
      <section id="medusa-game" className="relative max-w-6xl mx-auto px-5 sm:px-8 pb-16" data-testid="medusa-game">
        <div className="bg-[#0B0B10]/90 border border-fuchsia-500/25 rounded-3xl p-6 sm:p-8 backdrop-blur">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-fuchsia-400">Arcade Chamber</p>
              <h2 className="font-display text-xl sm:text-2xl font-bold text-white mt-1">Five Trials of the Serpent</h2>
            </div>
            <span className="inline-flex items-center gap-2 border border-yellow-500/40 text-yellow-300 rounded-full px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em]" data-testid="arcade-prize-note">
              <Gift size={12} /> Real prizes on every board
            </span>
          </div>
          <p className="text-sm text-ice/55 mb-8 max-w-2xl">Five games, five leaderboards. Post a score with your name and the label emails your prize — sample kits, one-time discount codes, even a free beat lease at the top tier.</p>

          {/* Trial I — Piano Scales */}
          <div className="mb-10">
            <div className="flex items-center gap-2.5 mb-4">
              <Gamepad2 size={16} style={{ color: GAME_META.piano.color }} />
              <p className="font-display text-white font-bold">I. Piano Scales</p>
            </div>
            <PianoScales engine={engine} />
          </div>

          {/* Trials II–V */}
          <div className="grid md:grid-cols-2 gap-6" data-testid="arcade-grid">
            <div>
              <div className="flex items-center gap-2.5 mb-4">
                <Gamepad2 size={16} style={{ color: GAME_META.quiz.color }} />
                <p className="font-display text-white font-bold">II. Venom Drop</p>
              </div>
              <VenomDropQuiz tracks={tracks} />
            </div>
            <div>
              <div className="flex items-center gap-2.5 mb-4">
                <Gamepad2 size={16} style={{ color: GAME_META.spiral.color }} />
                <p className="font-display text-white font-bold">III. Serpent Spiral</p>
              </div>
              <SerpentSpiral engine={engine} />
            </div>
            <div>
              <div className="flex items-center gap-2.5 mb-4">
                <Gamepad2 size={16} style={{ color: GAME_META.bass.color }} />
                <p className="font-display text-white font-bold">IV. Drop the Bass</p>
              </div>
              <DropTheBass />
            </div>
            <div>
              <div className="flex items-center gap-2.5 mb-4">
                <Gamepad2 size={16} style={{ color: GAME_META.memory.color }} />
                <p className="font-display text-white font-bold">V. Beat Builder</p>
              </div>
              <BeatBuilder engine={engine} />
            </div>
          </div>
        </div>
      </section>

      {/* HOW TO PLAY — quick ritual guide */}
      <section className="relative max-w-5xl mx-auto px-5 sm:px-8 pb-16" data-testid="medusa-howto">
        <div className="bg-[#0B0B10]/90 border border-teal-500/20 rounded-3xl p-6 sm:p-8 backdrop-blur">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-teal-300">How to Play</p>
              <h2 className="font-display text-xl sm:text-2xl font-bold text-white mt-1">The Ritual, Quick Version</h2>
            </div>
            <Link to="/medusa-guide" className="inline-flex items-center gap-2 border border-teal-500/50 text-teal-300 font-bold text-xs px-5 py-2.5 rounded-full hover:bg-teal-500/10 transition-all" data-testid="medusa-guide-link">
              <BookOpen size={13} /> Full Visitor Guide
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="medusa-howto-grid">
            {[
              ["Petrification Mixer", "Press Play the Beat, wake each stem from STONE to ALIVE, and twist the Venom FX pad — Dungeon Reverb, Tape Stop, Low-Pass."],
              ["Venom Meter", "Keep the beat playing. At 100% the room unlocks a reward: the SERPENT20 code and a free beat download."],
              ["Snake Pit", "Record up to 30 seconds of vocals right in the browser and send them to A&R. Featured sends become public Ghost Verses."],
              ["Arcade Chamber", "Five games, five leaderboards. Hit a prize tier and submit your name — the label emails your reward."],
              ["Crystal Vault", "Tap the crystals in the right order (the riddle is the key) to reveal the VIP Vault entrance."],
              ["Petrified Countdown", "One beat turns to stone at midnight every day. License it before the clock hits zero."],
            ].map(([title, body]) => (
              <div key={title} className="rounded-2xl border border-[#26262e] bg-black/40 p-4" data-testid={`howto-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <p className="font-display text-white text-sm font-bold">{title}</p>
                <p className="mt-1.5 text-xs text-ice/55 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CATALOG */}
      <section className="relative max-w-6xl mx-auto px-5 sm:px-8 pb-16" data-testid="medusa-catalog">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-teal-300">Exclusive Instrumentals</p>
        <h2 className="font-display text-xl sm:text-2xl font-bold text-white mt-1 mb-6">The Soundscape Vault — By Mood</h2>

        {petrified && (
          <div className={`mb-8 rounded-2xl border p-5 relative overflow-hidden ${petrifiedLocked ? "border-zinc-700" : "border-yellow-500/40 bg-yellow-950/10"}`} data-testid="medusa-petrified">
            <div className="flex flex-wrap items-center gap-4">
              <div className={petrifiedLocked ? "stone-locked" : ""}>
                <Gem size={30} className={petrifiedLocked ? "text-zinc-500" : "text-yellow-300"} />
              </div>
              <div className="flex-1 min-w-44">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-yellow-300">{petrifiedLocked ? "Petrified — gone forever" : "Vault-Locked Beat · expires at midnight"}</p>
                <p className="font-display text-white font-bold mt-0.5">{petrified.title}</p>
              </div>
              {!petrifiedLocked ? (
                <>
                  <div className="flex items-center gap-2 font-mono text-yellow-200 text-lg" data-testid="petrified-countdown">
                    <Timer size={16} /> {fmtClock(secondsLeft)}
                  </div>
                  <button onClick={() => setLicenseTrack(petrified)} className="inline-flex items-center gap-2 bg-yellow-400 text-black font-bold text-xs px-5 py-2.5 rounded-full hover:shadow-[0_0_20px_rgba(245,184,76,0.6)] active:scale-95 transition-all" data-testid="petrified-buy">
                    <Lock size={13} /> Secure Before It Turns to Stone
                  </button>
                </>
              ) : (
                <span className="font-mono text-xs text-zinc-500 uppercase tracking-[0.15em]" data-testid="petrified-locked-label">Stone. Forever.</span>
              )}
            </div>
          </div>
        )}

        {Object.entries(moodGroups).map(([mood, list]) => (
          <div key={mood} className="mb-8" data-testid={`mood-${mood.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-ice/45 mb-3">{mood}</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {list.map((t) => (
                <div key={t.id} className="bg-[#0B0B10] border border-[#22222a] hover:border-purple-500/50 rounded-2xl p-5 transition-all duration-300 group" data-testid={`medusa-beat-${t.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-display text-white font-bold truncate">{t.title}</p>
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {t.bpm && <span className="font-mono text-[9px] uppercase tracking-[0.1em] text-pink-300 border border-pink-500/40 rounded-full px-2 py-0.5">{t.bpm} BPM</span>}
                        {t.key && <span className="font-mono text-[9px] uppercase tracking-[0.1em] text-teal-300 border border-teal-500/40 rounded-full px-2 py-0.5">Key {t.key}</span>}
                        {t.exclusive && <span className="font-mono text-[9px] uppercase tracking-[0.1em] text-yellow-300 border border-yellow-500/40 rounded-full px-2 py-0.5">Exclusive avail.</span>}
                      </div>
                    </div>
                    <button onClick={() => togglePreview(t)} disabled={!t.file} className="shrink-0 w-11 h-11 rounded-full border border-purple-500/50 flex items-center justify-center text-purple-300 hover:bg-purple-500 hover:text-white disabled:opacity-30 transition-all" data-testid={`medusa-beat-play-${t.id}`} aria-label={`Preview ${t.title}`}>
                      {currentId === t.id && isPreviewPlaying ? <Pause size={16} /> : <Play size={16} />}
                    </button>
                  </div>
                  <button onClick={() => setLicenseTrack(t)} className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold text-xs px-5 py-3 rounded-full opacity-90 hover:opacity-100 hover:shadow-[0_0_22px_rgba(168,85,247,0.5)] active:scale-[0.98] transition-all" data-testid={`medusa-beat-buy-${t.id}`}>
                    <Lock size={13} /> Secure License / Buy
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}
        {!tracks.length && <p className="text-sm text-ice/45 py-8 text-center">Catalog loading…</p>}
      </section>

      {/* CRYSTAL RESONANCE VAULT */}
      <section className="relative max-w-5xl mx-auto px-5 sm:px-8 pb-16" data-testid="medusa-crystals">
        <div className={`relative overflow-hidden rounded-3xl border p-6 sm:p-8 backdrop-blur transition-colors duration-500 ${crystalState === "unlocked" ? "border-yellow-400/60 bg-yellow-950/10" : "border-teal-500/25 bg-[#0B0B10]/90"}`}>
          {crystalState === "unlocked" && <div className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-yellow-200/25 to-transparent pointer-events-none" style={{ animation: "vault-sweep 1.6s ease forwards" }} />}
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-teal-300">Crystal Resonance</p>
          <h2 className="font-display text-xl sm:text-2xl font-bold text-white mt-1">Crack the VIP Vault</h2>

          {crystalState !== "unlocked" ? (
            <div className="mt-5">
              <p className="text-sm text-ice/60 max-w-xl leading-relaxed" data-testid="medusa-crystal-hint">
                Three stones guard the vault. Strike them in the order of the old rite:
                <span className="text-purple-300"> the violet crown</span> opens the way,
                <span className="text-zinc-300"> the stone that drinks the light</span> follows,
                <span className="text-emerald-300"> the serpent's heart</span> seals it.
              </p>
              <div className="mt-6 flex flex-wrap gap-4" data-testid="medusa-crystal-grid">
                {CRYSTALS.map(({ id, label, color }) => {
                  const pickedAt = crystalPicks.indexOf(id);
                  return (
                    <button key={id} onClick={() => pickCrystal(id)} className={`flex-1 min-w-28 rounded-2xl border p-5 text-center transition-all duration-300 active:scale-95 ${crystalState === "wrong" ? "border-red-500/60" : ""}`}
                      style={{
                        borderColor: pickedAt >= 0 ? color : "#26262c",
                        boxShadow: pickedAt >= 0 ? `0 0 26px ${color}66` : "none",
                        animation: pickedAt >= 0 ? "crystal-shimmer 1.6s ease-in-out infinite" : "none",
                        color,
                      }}
                      data-testid={`crystal-${id}`}>
                      <Gem size={30} className="mx-auto" style={{ color }} />
                      <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em]">{label}</p>
                      <p className="text-[9px] font-mono text-ice/30 mt-0.5">{pickedAt >= 0 ? `STRIKE ${pickedAt + 1}` : "DORMANT"}</p>
                    </button>
                  );
                })}
              </div>
              {crystalState === "wrong" && <p className="mt-3 text-xs text-red-400 font-mono" data-testid="crystal-wrong">The stones reject that order. The vault resets…</p>}
            </div>
          ) : (
            <div className="mt-5" style={{ animation: "gold-reveal .7s ease" }} data-testid="medusa-vault-revealed">
              <p className="text-sm text-yellow-200/90 font-mono uppercase tracking-[0.15em] mb-4">The vault recognizes you.</p>
              {status?.member ? (
                vault === null ? (
                  <div className="flex justify-center py-8"><Loader2 className="animate-spin text-yellow-300" size={22} /></div>
                ) : vault.length === 0 ? (
                  <p className="text-sm text-ice/55">Fresh drops land here first — the next vault release is being mastered now.</p>
                ) : (
                  <div className="space-y-3" data-testid="medusa-member-vault">
                    {vault.map((it) => (
                      <div key={it.v_id} className="flex items-center gap-4 bg-black/40 border border-yellow-500/25 rounded-xl px-5 py-4">
                        {it.kind === "link" ? <ExternalLink size={15} className="text-yellow-300 shrink-0" /> : <MessageSquare size={15} className="text-yellow-300 shrink-0" />}
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-white truncate">{it.title}</p>
                          {it.kind === "note" && it.body && <p className="text-xs text-ice/55 mt-1 whitespace-pre-wrap">{it.body}</p>}
                        </div>
                        {it.kind === "link" && it.url && (
                          <a href={it.url} target="_blank" rel="noreferrer" className="shrink-0 inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.12em] border border-yellow-400/50 text-yellow-300 rounded-full px-4 py-2 hover:bg-yellow-400 hover:text-black transition-all" data-testid={`medusa-vault-open-${it.v_id}`}>Open</a>
                        )}
                      </div>
                    ))}
                  </div>
                )
              ) : (
                <div className="bg-black/40 border border-yellow-500/25 rounded-xl p-5">
                  <p className="text-sm text-ice/70 leading-relaxed">You've proven resonance — but the deepest shelves open only for Medusa Room VIP members. Early-access stems, project files, and unreleased downloads wait inside.</p>
                  <a href="#medusa-join" className="mt-4 inline-flex items-center gap-2 bg-gradient-to-r from-yellow-400 to-amber-500 text-black font-bold text-xs px-6 py-3 rounded-full hover:shadow-[0_0_24px_rgba(245,184,76,0.6)] transition-all" data-testid="medusa-vault-join-cta">
                    <Crown size={14} /> Unlock Full VIP Access
                  </a>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* MEMBERSHIP */}
      {!status?.member && (
        <section id="medusa-join" className="relative max-w-5xl mx-auto px-5 sm:px-8 pb-20" data-testid="medusa-plans">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-purple-300 text-center">Join the Inner Circle</p>
          <div className="grid sm:grid-cols-2 gap-5 max-w-3xl mx-auto mt-6">
            {PLANS.map((p) => (
              <div key={p.id} className={`relative rounded-2xl p-7 border ${p.featured ? "bg-[#14101C] border-purple-500/50 shadow-[0_0_40px_rgba(138,43,226,0.2)]" : "bg-[#0B0B10]/80 border-white/10"}`} data-testid={p.testId}>
                {p.trial && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-mono text-[10px] uppercase tracking-[0.15em] rounded-full px-3.5 py-1">7-Day Free Trial</span>}
                {p.featured && !p.trial && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-mono text-[10px] uppercase tracking-[0.15em] rounded-full px-3.5 py-1">Best Value</span>}
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ice/50">{p.name}</p>
                <p className="mt-3 font-display text-4xl font-bold text-white">{p.price}<span className="text-base text-ice/45 font-normal">{p.per}</span></p>
                <p className="mt-2 text-xs text-ice/50">{p.note}</p>
                <ul className="mt-5 space-y-2.5">
                  {PERKS.map((perk) => (
                    <li key={perk} className="flex items-start gap-2.5 text-sm text-ice/70">
                      <Check size={15} className="text-emerald-400 shrink-0 mt-0.5" /> {perk}
                    </li>
                  ))}
                </ul>
                <button onClick={() => subscribe(p.id)} disabled={!!busy} className={`mt-7 w-full inline-flex items-center justify-center gap-2 font-bold text-sm px-6 py-3.5 rounded-full active:scale-[0.98] disabled:opacity-60 transition-all ${p.featured ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:shadow-[0_0_26px_rgba(168,85,247,0.55)]" : "border border-purple-500/50 text-purple-300 hover:bg-purple-500 hover:text-white"}`} data-testid={`medusa-subscribe-${p.id}`}>
                  {busy === p.id ? <Loader2 size={16} className="animate-spin" /> : user ? <Crown size={16} /> : <LogIn size={16} />}
                  {busy === p.id ? "Opening checkout…" : p.trial ? (user ? "Start Free Trial — Monthly" : "Sign in & Start Free Trial") : (user ? `Join — ${p.name}` : "Sign in & Join")}
                </button>
              </div>
            ))}
          </div>
          {error && <p className="mt-5 text-center text-sm text-red-400" data-testid="medusa-error">{error}</p>}
          {!user && <p className="mt-5 text-center text-xs text-ice/45">Membership ties to your free DGE account — <button onClick={signInWithGoogle} className="text-purple-300 hover:text-pink-300 underline underline-offset-2">sign in with Google</button> first.</p>}
        </section>
      )}

      <section className="max-w-5xl mx-auto px-5 sm:px-8 pb-24 text-center">
        <p className="text-xs text-ice/40">
          Questions about the room? <Link to="/quote" className="text-purple-300 hover:text-pink-300 transition-colors">Contact the label</Link> — memberships can be canceled anytime.
          Read the <Link to="/medusa-terms" className="text-purple-300 hover:text-pink-300 transition-colors" data-testid="medusa-terms-link">Room Rules & Terms</Link> or the <Link to="/medusa-guide" className="text-purple-300 hover:text-pink-300 transition-colors" data-testid="medusa-guide-link-bottom">Visitor's Guide</Link>.
        </p>
      </section>

      {/* purple haze smoke — drifts along the floor of the room, above content, below the ticker */}
      <div className="fixed bottom-6 left-0 w-full h-44 pointer-events-none overflow-hidden z-30" aria-hidden="true" data-testid="medusa-smoke">
        <div className="absolute inset-0 w-[110%] h-full opacity-40 mix-blend-screen animate-smoke-drift"
             style={{ backgroundImage: "url('/smoke-haze.png')", backgroundSize: "cover", backgroundPosition: "bottom" }} />
        <div className="absolute inset-0 w-[110%] h-full opacity-25 mix-blend-screen animate-smoke-drift-slow"
             style={{ backgroundImage: "url('/smoke-haze.png')", backgroundSize: "cover", backgroundPosition: "bottom", transform: "scaleX(-1)" }} />
      </div>

      {/* ACTIVITY TICKER */}
      <div className="fixed bottom-0 inset-x-0 z-40 bg-[#07070a]/95 border-t border-purple-500/25 backdrop-blur py-2.5 overflow-hidden" data-testid="medusa-ticker">
        <div className="medusa-marquee-track">
          {[...TICKER_ITEMS, ...TICKER_ITEMS].map((msg, i) => (
            <span key={i} className="mx-8 font-mono text-[10px] uppercase tracking-[0.18em] text-ice/50 inline-flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(57,255,20,0.8)]" /> {msg}
            </span>
          ))}
        </div>
      </div>

      <LicenseModal track={licenseTrack} onClose={() => setLicenseTrack(null)} onBuy={buy} buying={buying}
        onRequestQuote={() => { window.location.href = "/quote?service=Beat%20Licensing"; }} />
    </main>
  );
}
